import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { styles } from '../../../../styles/themes/style'
import { IPrice } from '../../../../interfaces/models/billingPlan'
import { formatCurrency, formatPrice, splitFormattedPriceTwoDecimals } from '../../../../utils'
import PricingIcon from './PricingIcon'
import PlanFeatureList from './PlanFeatureList'
import { PlanCard, PlanCardHeader, PlanDescription, PlanFooter, PlanFooterNote, PlanPriceText, PlanTitle, PlanTitleBlock, PlanTitleRow, PrimaryCta } from './PlanCard'
import { growthFeatures } from '../pricingV2Content'
import { GROWTH_PLAN_DESCRIPTION, GROWTH_PLAN_NAME } from '../pricingV2'

interface IGrowthPlanCardProps {
    selectedPrice: IPrice
    /** Yearly price minus 12 monthly payments, in cents. */
    yearlySaving: number
    saveLabel?: string
    isCurrentPlan?: boolean
    onBuy: () => void
}

const GrowthPlanCard: FunctionComponent<IGrowthPlanCardProps> = ({ selectedPrice, yearlySaving, saveLabel, isCurrentPlan, onBuy }) => {
    const isYearly = selectedPrice.interval === 'year'
    const currencySymbol = formatCurrency(selectedPrice.currency)
    const { integerPart, decimalPart } = splitFormattedPriceTwoDecimals(isYearly ? selectedPrice.price / 12 : selectedPrice.price)

    return (
        <PlanCard $highlighted>
            <PopularBadge><PricingIcon type="crown" />Most popular</PopularBadge>
            <PlanCardHeader>
                <PlanTitleBlock>
                    <PlanTitleRow>
                        <PlanTitle>{GROWTH_PLAN_NAME}</PlanTitle>
                        {isYearly && saveLabel && <SaveBadge>{saveLabel}</SaveBadge>}
                    </PlanTitleRow>
                    <PlanDescription>{GROWTH_PLAN_DESCRIPTION}</PlanDescription>
                </PlanTitleBlock>
                <PriceBlock>
                    <PriceRow>
                        <Amount>
                            {currencySymbol}{integerPart}
                            {decimalPart !== '00' && <Decimals>.{decimalPart}</Decimals>}
                        </Amount>
                        <PriceUnit>/ per user / month</PriceUnit>
                    </PriceRow>
                    <BilledText>
                        {isYearly
                            ? `${currencySymbol}${formatPrice(selectedPrice.price)} billed yearly${yearlySaving > 0 ? `, saving you ${currencySymbol}${Math.floor(yearlySaving / 100).toLocaleString()}/year per user` : ''}`
                            : 'Billed monthly'}
                    </BilledText>
                </PriceBlock>
            </PlanCardHeader>
            <PlanFeatureList features={growthFeatures} />
            <PlanFooter>
                <PrimaryCta type="button" disabled={isCurrentPlan} onClick={onBuy}>
                    {isCurrentPlan ? 'Your plan' : <>Buy now<PricingIcon type="arrowRight" /></>}
                </PrimaryCta>
                <PlanFooterNote>Cancel anytime</PlanFooterNote>
            </PlanFooter>
        </PlanCard>
    )
}

const PopularBadge = styled.span`
    position: absolute;
    top: -14px;
    left: 20px;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 12px;
    border-radius: 999px;
    background-color: ${styles.colors.primary600};
    color: ${styles.colors.white};
    font-size: 13px;
    font-weight: 700;
    line-height: 19px;

    svg {
        width: 15px;
        height: 15px;
    }
`
const SaveBadge = styled.span`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    height: 22px;
    margin-top: 3px;
    padding: 0 8px;
    border-radius: 999px;
    background-color: rgba(0, 188, 125, 0.1);
    color: #009966;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
`
const PriceBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`
const PriceRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`
const Amount = styled(PlanPriceText)`
    display: flex;
    align-items: flex-start;
`
const Decimals = styled.span`
    margin: 4px 0 0 3px;
    font-size: 20px;
    line-height: 20px;
    letter-spacing: 0;
`
const PriceUnit = styled.span`
    font-size: 13px;
    line-height: 18px;
    color: ${styles.colors.black400};
`
const BilledText = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 18px;
    color: ${styles.colors.black400};
`

export default GrowthPlanCard
